import { createContext, useContext, useEffect, useState } from 'react';
import {
  authApi, getToken, setToken, clearSession, getBranchId, setBranchId,
} from './api';
import { DocumentLocalizer } from './i18n';
import { isPublicBranch, publicBranchName } from './public/PublicBrand';

const AuthContext = createContext(null);

// The database remains authoritative and untouched. This is deliberately only
// the fixed branch list exposed by POS selectors and branch-scoped screens.
const publicPosBranches = (branches = []) => branches
  .filter(isPublicBranch)
  .map((branch) => ({ ...branch, displayName: publicBranchName(branch) }));

const selectedBranchFor = (branches, requestedId) => {
  const requested = branches.find((branch) => String(branch.id) === String(requestedId));
  return requested || branches.find((branch) => branch.isDefault) || branches[0] || null;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranch] = useState(getBranchId());
  const [loading, setLoading] = useState(Boolean(getToken()));
  const [locale, setLocale] = useState('th');

  // Rehydrate on reload. The token lives in sessionStorage, so this only ever
  // runs within the same tab that logged in.
  useEffect(() => {
    if (!getToken()) return;
    let cancelled = false;

    authApi
      .me()
      .then((data) => {
        if (cancelled) return;
        const visibleBranches = publicPosBranches(data.branches);
        const selected = selectedBranchFor(visibleBranches, getBranchId());
        setUser(data.user);
        setLocale(data.user?.locale || 'th');
        setOrganization(data.organization);
        setBranches(visibleBranches);
        if (selected && String(selected.id) !== String(getBranchId())) {
          setBranchId(selected.id);
          setBranch(selected.id);
        } else if (!selected) {
          setBranchId(null);
          setBranch(null);
        }
      })
      .catch(() => clearSession())
      .finally(() => !cancelled && setLoading(false));

    return () => { cancelled = true; };
  }, []);

  /** Re-reads the session — the โปรไฟล์ page calls this after บันทึก so the
      header picks up a renamed user without a reload. */
  const refresh = async () => {
    const data = await authApi.me();
    const visibleBranches = publicPosBranches(data.branches);
    const selected = selectedBranchFor(visibleBranches, branchId);
    setUser(data.user);
    setLocale(data.user?.locale || 'th');
    setOrganization(data.organization);
    setBranches(visibleBranches);
    if (selected && String(selected.id) !== String(branchId)) {
      setBranchId(selected.id);
      setBranch(selected.id);
    } else if (!selected) {
      setBranchId(null);
      setBranch(null);
    }
    return data;
  };

  const login = async (identifier, password) => {
    const data = await authApi.login(identifier, password);
    const visibleBranches = publicPosBranches(data.branches);
    const selected = selectedBranchFor(visibleBranches, data.defaultBranchId);
    setToken(data.token);
    setBranchId(selected?.id || null);
    setUser(data.user);
    setLocale(data.user?.locale || 'th');
    setOrganization(data.organization || null);
    setBranches(visibleBranches);
    setBranch(selected?.id || null);
    return data;
  };

  const logout = () => {
    clearSession();
    setUser(null);
    setOrganization(null);
    setBranches([]);
    setBranch(null);
    setLocale('th');
  };

  const switchBranch = (id) => {
    setBranchId(id);
    setBranch(id);
  };

  const switchOrganization = async (organizationId) => {
    const data = await authApi.switchOrganization(organizationId);
    const visibleBranches = publicPosBranches(data.branches);
    const selected = selectedBranchFor(visibleBranches, data.defaultBranchId);
    setToken(data.token);
    setBranchId(selected?.id || null);
    setUser(data.user);
    setLocale(data.user?.locale || 'th');
    setOrganization(data.organization);
    setBranches(visibleBranches);
    setBranch(selected?.id || null);
    return data;
  };

  const switchLocale = async (nextLocale) => {
    const previous = locale;
    setLocale(nextLocale);
    setUser((current) => (current ? { ...current, locale: nextLocale } : current));
    try {
      await authApi.updateLocale(nextLocale);
    } catch (error) {
      setLocale(previous);
      setUser((current) => (current ? { ...current, locale: previous } : current));
      throw error;
    }
  };

  const value = {
    user,
    organization,
    branches,
    branchId,
    branch: branches.find((b) => b.id === branchId) || null,
    loading,
    locale,
    isAuthenticated: Boolean(user),
    login,
    logout,
    refresh,
    switchBranch,
    switchOrganization,
    switchLocale,
  };

  return (
    <AuthContext.Provider value={value}>
      <DocumentLocalizer locale={locale}>{children}</DocumentLocalizer>
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
